import {
  Component,
  PLATFORM_ID,
  inject,
  signal,
  computed,
} from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { isPlatformBrowser } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { DataService } from "./data";
import { Icon } from "./icon";
import { Reveal } from "./reveal";
import { environment } from "../environments/environment";
import { Analytics } from "./analytics";

const FAQS = [
  {
    q: "Do I need a visa for Sri Lanka?",
    a: "Most nationalities need an Electronic Travel Authorization (ETA) before arrival - it takes minutes to apply for online. We send exact instructions for your passport once your trip is booked.",
  },
  {
    q: "When's the best time to visit?",
    a: "It depends which coast you're routing through: December–March is driest on the west and south coasts (Colombo, Galle, hill country); April–September is driest on the east coast. We plan around this either way.",
  },
  {
    q: "How far ahead should I book?",
    a: "Four to six weeks is comfortable for most itineraries. During peak season (December–March) and for our longer circuits like Panoramic Tours in Sri Lanka, eight weeks gives us better hotel availability.",
  },
  {
    q: "Can you customize any of these itineraries?",
    a: "Every package here is a starting point, not a fixed menu. Tell us your dates, pace, and interests in the form below and we'll route something specific to you.",
  },
];

@Component({
  selector: "app-contact",
  imports: [RouterLink, Icon, Reveal, FormsModule],
  template: `
    <section class="grain relative pt-40 pb-20 surface-ink overflow-hidden">
      <div
        class="mesh-blob mesh-blob-a w-[420px] h-[420px] -top-32 -right-20 bg-saffron/15"
      ></div>
      <div
        class="mesh-blob mesh-blob-b w-[300px] h-[300px] bottom-0 left-0 bg-teal/10"
      ></div>
      <div class="container relative z-10">
        <div class="flex items-center gap-2 mb-6" appReveal="fade">
          <div class="w-8 h-[2px] bg-saffron"></div>
          <span
            class="text-saffron font-bold uppercase tracking-[0.3em] text-[10px]"
            >Get In Touch</span
          >
        </div>
        <h1
          class="text-5xl md:text-7xl font-serif leading-[0.95] tracking-tight max-w-3xl"
          appReveal
          [revealDelay]="0.1"
        >
          Let's route
          <span class="italic font-light text-paper/50">your journey.</span>
        </h1>
        <p
          class="text-paper/60 max-w-xl mt-6 text-lg leading-relaxed"
          appReveal
          [revealDelay]="0.2"
        >
          Tell us where you'd like to go and we'll reply within one business day
          with a detailed itinerary and a real quote in USD - no obligation.
        </p>
      </div>
    </section>

    <section class="relative -mt-12 pb-32">
      <div class="container">
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <!-- Form -->
          <div
            class="lg:col-span-3 bg-paper rounded-[2rem] shadow-2xl p-8 sm:p-12"
            appReveal="scale"
          >
            @if (selectedTour(); as tour) {
              <div
                class="flex items-center gap-3 mb-8 px-4 py-3 rounded-2xl bg-saffron/15 border border-saffron/30"
              >
                <app-icon
                  name="route"
                  [size]="16"
                  class="text-vermilion shrink-0"
                />
                <span class="text-sm">
                  Enquiring about
                  <strong class="font-semibold">{{ tour.title }}</strong>
                  <span class="route-code text-vermilion">
                    · from &#36;{{ tour.price }}</span
                  >
                </span>
              </div>
            }

            @if (!submitted()) {
              <form (submit)="onSubmit($event)" class="space-y-6">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label
                      for="name"
                      class="block text-[10px] uppercase tracking-widest font-bold text-ink/50 mb-2"
                      >Full Name</label
                    >
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      [(ngModel)]="form.name"
                      class="w-full bg-transparent border-b-2 border-sand py-2.5 focus:outline-none focus:border-vermilion transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      for="email"
                      class="block text-[10px] uppercase tracking-widest font-bold text-ink/50 mb-2"
                      >Email</label
                    >
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      [(ngModel)]="form.email"
                      class="w-full bg-transparent border-b-2 border-sand py-2.5 focus:outline-none focus:border-vermilion transition-colors"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label
                      for="tour"
                      class="block text-[10px] uppercase tracking-widest font-bold text-ink/50 mb-2"
                      >Tour of Interest</label
                    >
                    <select
                      id="tour"
                      name="tour"
                      [(ngModel)]="form.tourId"
                      class="w-full bg-transparent border-b-2 border-sand py-2.5 focus:outline-none focus:border-vermilion transition-colors appearance-none"
                    >
                      <option value="">Not sure yet - surprise me</option>
                      @for (t of tours(); track t.id) {
                        <option [value]="t.id">
                          {{ t.title }} ({{ t.duration }})
                        </option>
                      }
                    </select>
                  </div>
                  <div>
                    <label
                      for="travelers"
                      class="block text-[10px] uppercase tracking-widest font-bold text-ink/50 mb-2"
                      >Travelers</label
                    >
                    <select
                      id="travelers"
                      name="travelers"
                      [(ngModel)]="form.travelers"
                      class="w-full bg-transparent border-b-2 border-sand py-2.5 focus:outline-none focus:border-vermilion transition-colors appearance-none"
                    >
                      <option>1 traveler</option>
                      <option selected>2 travelers</option>
                      <option>3 travelers</option>
                      <option>4+ travelers</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    for="dates"
                    class="block text-[10px] uppercase tracking-widest font-bold text-ink/50 mb-2"
                    >Preferred Travel Dates</label
                  >
                  <input
                    id="dates"
                    name="dates"
                    type="text"
                    placeholder="e.g. Feb 2027, flexible"
                    [(ngModel)]="form.dates"
                    class="w-full bg-transparent border-b-2 border-sand py-2.5 focus:outline-none focus:border-vermilion transition-colors placeholder:text-ink/25"
                  />
                </div>

                <div>
                  <label
                    for="message"
                    class="block text-[10px] uppercase tracking-widest font-bold text-ink/50 mb-2"
                    >Tell us about the trip</label
                  >
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    placeholder="Interests, pace, special occasions, accessibility needs…"
                    [(ngModel)]="form.message"
                    class="w-full bg-transparent border-b-2 border-sand py-2.5 focus:outline-none focus:border-vermilion transition-colors resize-none placeholder:text-ink/25"
                  ></textarea>
                </div>

                <button type="submit" class="btn-vermilion w-full sm:w-auto">
                  Send Enquiry <app-icon name="send" [size]="16" />
                </button>
                <p class="text-xs text-ink/40 leading-relaxed">
                  This opens your email app with the details filled in,
                  addressed to
                  {{ email }}. Nothing sends automatically - you're always in
                  control of what goes out.
                </p>
              </form>
            } @else {
              <div class="py-16 text-center" appReveal="scale">
                <div
                  class="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center bg-teal/15"
                >
                  <app-icon name="check" [size]="26" class="text-teal" />
                </div>
                <h3 class="text-3xl font-serif mb-3">
                  Your email app should be open
                </h3>
                <p class="text-ink/60 max-w-md mx-auto leading-relaxed">
                  If nothing opened, write to us directly at
                  <a
                    [href]="mailtoHref()"
                    class="text-vermilion font-semibold underline underline-offset-2"
                    >{{ email }}</a
                  >
                  - we reply within one business day.
                </p>
                <button
                  type="button"
                  (click)="submitted.set(false)"
                  class="btn-outline mt-8 mx-auto"
                >
                  Start Over
                </button>
              </div>
            }
          </div>

          <!-- Side info -->
          <div class="lg:col-span-2 space-y-6">
            <div
              class="bg-paper-soft rounded-[1.75rem] p-8 space-y-6"
              appReveal
              [revealDelay]="0.1"
            >
              <div class="flex items-start gap-4">
                <app-icon
                  name="clock"
                  [size]="18"
                  class="text-vermilion mt-0.5 shrink-0"
                />
                <div>
                  <div class="font-semibold text-sm mb-1">Response Time</div>
                  <div class="text-ink/60 text-sm">
                    Within one business day, every day of the week
                  </div>
                </div>
              </div>
              <div class="flex items-start gap-4">
                <app-icon
                  name="mail"
                  [size]="18"
                  class="text-vermilion mt-0.5 shrink-0"
                />
                <div>
                  <div class="font-semibold text-sm mb-1">Email</div>
                  <a
                    [href]="mailtoHref()"
                    class="text-ink/60 text-sm hover:text-vermilion transition-colors break-all"
                    >{{ email }}</a
                  >
                </div>
              </div>
              <div class="flex items-start gap-4">
                <app-icon
                  name="phone"
                  [size]="18"
                  class="text-vermilion mt-0.5 shrink-0"
                />
                <div>
                  <div class="font-semibold text-sm mb-1">Phone / WhatsApp</div>
                  <a
                    [href]="'tel:' + phoneHref"
                    class="text-ink/60 text-sm hover:text-vermilion transition-colors"
                    >{{ phone }}</a
                  >
                </div>
              </div>
              <div class="flex items-start gap-4">
                <app-icon
                  name="pin"
                  [size]="18"
                  class="text-vermilion mt-0.5 shrink-0"
                />
                <div>
                  <div class="font-semibold text-sm mb-1">Studio</div>
                  <div class="text-ink/60 text-sm">Colombo 03, Sri Lanka</div>
                </div>
              </div>
            </div>

            <div
              class="grain bg-ink text-paper rounded-[1.75rem] p-8 relative overflow-hidden"
              appReveal
              [revealDelay]="0.2"
            >
              <div
                class="mesh-blob mesh-blob-a w-[220px] h-[220px] -top-16 -right-16 bg-saffron/20"
              ></div>
              <div class="relative z-10">
                <div class="route-code text-saffron text-xs mb-3">
                  NOT READY TO WRITE IN?
                </div>
                <p class="text-paper/70 text-sm leading-relaxed mb-5">
                  Browse the full route list first - every package links
                  straight back here with your trip pre-filled.
                </p>
                <a
                  routerLink="/tours"
                  class="inline-flex items-center gap-2 text-saffron font-semibold text-sm hover:gap-3 transition-all"
                >
                  View All Tours <app-icon name="arrow-right" [size]="14" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- FAQ -->
        <div class="mt-28 max-w-3xl">
          <h2 class="text-3xl md:text-4xl font-serif mb-10" appReveal="fade">
            Before you write in
          </h2>
          <div class="divide-y divide-sand/60 border-t border-b border-sand/60">
            @for (faq of faqs; track faq.q; let i = $index) {
              <div appReveal="fade" [revealDelay]="i * 0.05">
                <button
                  type="button"
                  (click)="toggleFaq(i)"
                  class="w-full flex items-center justify-between gap-6 py-6 text-left"
                >
                  <span class="font-serif text-xl">{{ faq.q }}</span>
                  <app-icon
                    name="chevron-down"
                    [size]="18"
                    class="shrink-0 transition-transform"
                    [style.transform]="
                      openFaq() === i ? 'rotate(180deg)' : 'none'
                    "
                  />
                </button>
                @if (openFaq() === i) {
                  <p class="text-ink/60 leading-relaxed pb-6 pr-10">
                    {{ faq.a }}
                  </p>
                }
              </div>
            }
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class Contact {
  private route = inject(ActivatedRoute);
  private data = inject(DataService);
  private platformId = inject(PLATFORM_ID);
  private analytics = inject(Analytics);

  email = environment.contactEmail;
  phone = environment.contactPhone;
  phoneHref = environment.contactPhoneHref;
  faqs = FAQS;
  tours = this.data.getTours();

  submitted = signal(false);
  openFaq = signal<number | null>(0);

  form = {
    name: "",
    email: "",
    tourId: "",
    travelers: "2 travelers",
    dates: "",
    message: "",
  };

  topic = signal("");

  selectedTour = computed(() =>
    this.tours().find((t) => t.id === this.form.tourId),
  );

  constructor() {
    this.route.queryParamMap.subscribe((params) => {
      const tourId = params.get("tour");
      if (tourId && this.tours().some((t) => t.id === tourId)) {
        this.form.tourId = tourId;
      }
      const topic = params.get("topic");
      if (topic) {
        this.topic.set(topic);
        if (!this.form.message) this.form.message = `I'm interested in: ${topic}\n\n`;
      }
    });
  }

  toggleFaq(i: number) {
    this.openFaq.update((v) => (v === i ? null : i));
  }

  mailtoHref() {
    return `mailto:${this.email}`;
  }

  onSubmit(e: Event) {
    e.preventDefault();
    const tour = this.selectedTour();
    const subject = `Trip Enquiry - ${tour ? tour.title : this.topic() || "Custom Itinerary"}`;
    const bodyLines = [
      `Name: ${this.form.name}`,
      `Email: ${this.form.email}`,
      `Tour: ${tour ? tour.title : "Not sure yet"}`,
      `Travelers: ${this.form.travelers}`,
      `Preferred dates: ${this.form.dates || "Flexible"}`,
      "",
      this.form.message || "(No additional details provided)",
    ];
    const url = `mailto:${this.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;

    if (isPlatformBrowser(this.platformId)) {
      window.location.href = url;
    }
    this.analytics.trackEvent("generate_lead", {
      value: tour?.price,
      currency: "USD",
      tour_id: tour?.id ?? "custom_itinerary",
      tour_title: tour?.title ?? "Custom Itinerary",
    });
    this.submitted.set(true);
  }
}
